import React, { useEffect, useState } from 'react';
import { Drawer } from '@dsg-tech/homefield-react';
import { MY_ACCOUNT_LISTS_URL } from 'utility/common-utils';
import { clearListsStore, setView, setTarget, setSuccessCallback } from 'store/slices/listsPanel';
import { useAppDispatch, useAppSelector } from 'store/store';
import useLists from 'hooks/useLists';
import {
  successCallbackSelector,
  targetSelector,
  viewSelector,
} from 'store/selectors/listsPanelSelector';
import { getFeatureFlagByNameSelector, storeIdentifierNameSelector } from 'store/selectors';
import { favoritesListsSelector } from 'store/selectors/userFavoritesSelector';
import {
  ecodeParamSelector,
  optionalParamsSelector,
  referrerParamSelector,
} from 'store/selectors/quickViewSelector';
import { multiListAddRemoveFromFavoritesEvent } from '../Quickview/analytics/analytics-events';
import { TargetType } from '../../types';
import './ListsPanel.scss';
import ListForm from './components/ListForm';
import ProductsOnListsForm from './components/ProductsOnListsForm';

const ListsPanel = () => {
  const storeIdentifierName = useAppSelector(storeIdentifierNameSelector);
  const [storeReady, setStoreReady] = useState(false);
  const [defaultFlag, setDefaultFlag] = useState(false);
  const [calledFrom, setCalledFrom] = useState<'PLP' | 'PDP'>('PLP');
  const { fetchLists, deleteFromLists, fetchListsFromProductsPromise } =
    useLists(storeIdentifierName);
  const dispatch = useAppDispatch();
  const view = useAppSelector(viewSelector);
  const target = useAppSelector(targetSelector);
  const athleteLists = useAppSelector(favoritesListsSelector);
  const successCallback = useAppSelector(successCallbackSelector);
  const ecodeParam = useAppSelector(ecodeParamSelector);
  const referrerParam = useAppSelector(referrerParamSelector);
  const optionalParams = useAppSelector(optionalParamsSelector);

  const quickViewDrawerEnabled = useAppSelector(
    getFeatureFlagByNameSelector('quickViewDrawerEnabled'),
  );

  const [open, setOpen] = useState(false);

  const setTitle = view === 'list' ? 'List Setting' : 'Manage Your Saves';

  const onClose = () => {
    dispatch(clearListsStore());
    setOpen(false);
    setStoreReady(false);
  };

  const openListPanel = (
    template: 'add-product' | 'remove-product',
    product: TargetType,
    defaultList = false,
    successCallbackParam = () => {},
    from?: 'PLP' | 'PDP',
  ) => {
    if (from) {
      setCalledFrom(from);
    }
    setDefaultFlag(defaultList);
    dispatch(setTarget(product));
    dispatch(setSuccessCallback(successCallbackParam));
    dispatch(setView(template));
    setStoreReady(false);
  };

  useEffect(() => {
    if (view === 'add-product' || view === 'remove-product') {
      fetchLists(() => {
        setStoreReady(true);
      });
    }
  }, [view]);

  useEffect(() => {
    if (!storeReady || view === '') {
      return;
    }

    const plpRemove = view === 'remove-product' && storeReady && calledFrom === 'PLP';

    if (plpRemove) {
      (async () => {
        const listsPerProduct = await fetchListsFromProductsPromise(target);
        if (!listsPerProduct.length) {
          const evt = new CustomEvent('renderToasty', {
            detail: {
              linkMessage: {
                intro: 'Product not found.',
                url: '',
                caption: '',
              },
            },
          });
          window.dispatchEvent(evt);
          return;
        }

        if (listsPerProduct.length === 1) {
          const { productID, listID } = listsPerProduct[0];
          const { displayName } = athleteLists.find((al) => al.id === listID.toString());
          const eventUrl = `${MY_ACCOUNT_LISTS_URL}/${displayName
            .replace(/(?:^\w|[A-Z]|\b\w)/g, (letter) => letter)
            .replace(/\s+/g, '')}?listID=${listID}`;

          deleteFromLists(
            [productID],
            () => {
              successCallback({ added: [], removed: [productID.toString()] });
              multiListAddRemoveFromFavoritesEvent(target, 1, 'remove');

              const evt = new CustomEvent('renderToasty', {
                detail: {
                  linkMessage: {
                    intro: 'Removed from ',
                    url: eventUrl,
                    caption: displayName,
                  },
                },
              });
              window.dispatchEvent(evt);
              dispatch(clearListsStore());
              setStoreReady(false);
            },
            () => {
              const evt = new CustomEvent('renderToasty', { detail: 'Unknown' });
              window.dispatchEvent(evt);
              dispatch(clearListsStore());
              setStoreReady(false);
            },
          );
          return;
        }
        setOpen(true);
      })();
    } else if (view !== 'list') {
      setOpen(true);
    }
  }, [view, athleteLists, target, storeReady, defaultFlag, calledFrom]);

  useEffect(() => {
    window.headerFunctions = {
      ...(window.headerFunctions && { ...window.headerFunctions }),
      openListPanel,
    };

    window.addEventListener('updateFavoritesListStoreData', () => {
      fetchLists();
    });
  }, []);

  const handleBackButton = () => {
    if (view === 'list') {
      dispatch(setView('add-product'));
    } else {
      setOpen(false);
      window.quickview.openProductQuickView(ecodeParam, referrerParam, optionalParams);
    }
  };

  // The back button will be enabled in specific scenarios:
  // When the listPanel opens after adding to favorites or adding to the cart
  // When quickViewDrawerEnabled is true
  // When the view is add-product because remove-product occurs on the PLP when a product is in more than two lists, and the listPanel will open directly

  const isBackButtonEnabled =
    quickViewDrawerEnabled && calledFrom === 'PLP' && view !== 'remove-product';

  return (
    <Drawer
      open={open}
      className="lists-panel"
      onCloseModal={onClose}
      title={setTitle}
      onBackButton={handleBackButton}
      stickyCloseButton
      backButton={isBackButtonEnabled}>
      <div className="lists-panel-content hmf-modal-container">
        <div className="lists-panel-wrapper hmf-display-flex hmf-flex-column ">
          <div className="lists-panel-body hmf-pt-s hmf-display-flex hmf-flex-column hmf-align-items-center">
            {view === 'list' && <ListForm />}
            {(view === 'add-product' || view === 'remove-product') && (
              <ProductsOnListsForm onClose={onClose} />
            )}
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default ListsPanel;
